/**
 * DRPX Courier — Push-Subscriptions & Send-Push (Supabase)
 *
 * CRUD für die `push_subscriptions`-Tabelle (Migration 038) und
 * Aufruf der `send-push` Edge Function. Speichert Web-Push-Abonnements
 * pro Gerät und User.
 *
 * RLS: Migration 038 garantiert, dass User nur eigene Subscriptions
 * sehen/ändern/löschen können.
 *
 * @module lib/supabase/push
 */

import { _sb } from './client.js';

// ════════════════════════════════════════════════════════════════════
// Interne Helper
// ════════════════════════════════════════════════════════════════════

const NO_CLIENT_ERROR = new Error('Supabase client not initialized');

// ════════════════════════════════════════════════════════════════════
// Lokale Interfaces (nicht exportiert)
// ════════════════════════════════════════════════════════════════════

/** Subscription-Daten aus `push.ts` → `subscribeToPush()`. */
interface PushSubscriptionInput {
  endpoint: string;
  p256dh: string;
  auth: string;
}

/** Settings-Update für eine Push-Subscription (DB-Spaltennamen). */
interface PushSettingsUpdate {
  enabled?: boolean;
  notify_auftraege?: boolean;
  notify_chat?: boolean;
  notify_erinnerungen?: boolean;
  quiet_hours_enabled?: boolean;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  quiet_hours_allow_urgent?: boolean;
  [key: string]: unknown;
}

/** Payload für die `send-push` Edge Function. */
interface SendPushPayload {
  user_id: string;
  group: 'auftraege' | 'chat' | 'erinnerungen';
  title: string;
  body: string;
  url?: string;
  tag?: string;
  urgent?: boolean;
}

// ════════════════════════════════════════════════════════════════════
// PUSH SUBSCRIPTIONS
// ════════════════════════════════════════════════════════════════════

/**
 * Holt alle Push-Subscriptions des aktuellen Users (alle Geräte).
 */
export async function getMyPushSubscriptions(): Promise<{
  data: Array<Record<string, unknown>>;
  error?: unknown;
}> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('push_subscriptions')
      .select('*')
      .order('created_at', { ascending: false });
    return {
      data: (r.data || []) as Array<Record<string, unknown>>,
      error: r.error ?? undefined,
    };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/**
 * Speichert (oder aktualisiert) eine Push-Subscription für den aktuellen
 * User. Upsert auf `endpoint` (UNIQUE-Constraint aus Migration 038).
 */
export async function savePushSubscription(
  subscription: PushSubscriptionInput,
  deviceLabel?: string,
): Promise<{ data?: Record<string, unknown>; error?: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const sessRes = await client.auth.getSession();
    const userId = sessRes?.data?.session?.user?.id || null;
    if (!userId) return { error: 'not_authenticated' };

    if (!subscription || !subscription.endpoint) {
      return { error: 'invalid_subscription' };
    }

    const r = await client
      .from('push_subscriptions')
      .upsert(
        {
          user_id: userId,
          endpoint: subscription.endpoint,
          p256dh: subscription.p256dh,
          auth: subscription.auth,
          device_label: deviceLabel || null,
          enabled: true,
          last_used_at: new Date().toISOString(),
        },
        { onConflict: 'endpoint' },
      )
      .select()
      .single();

    return {
      data: r.data as Record<string, unknown> | undefined,
      error: r.error ?? undefined,
    };
  } catch (e: unknown) {
    return { error: e };
  }
}

/**
 * Löscht eine Push-Subscription anhand des Endpoints.
 */
export async function deletePushSubscriptionByEndpoint(
  endpoint: string,
): Promise<{ error?: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    if (!endpoint) return { error: 'endpoint_missing' };
    const r = await client
      .from('push_subscriptions')
      .delete()
      .eq('endpoint', endpoint);
    return { error: r.error ?? undefined };
  } catch (e: unknown) {
    return { error: e };
  }
}

/**
 * Aktualisiert die Push-Settings einer Subscription (Master-Schalter,
 * Gruppen-Toggles, Quiet Hours).
 *
 * Eigene Whitelist (DB-Spaltennamen, kein `camelToSnake` nötig).
 */
export async function updatePushSettings(
  id: string,
  settings: PushSettingsUpdate,
): Promise<{ data?: Record<string, unknown>; error?: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    if (!id) return { error: 'id_missing' };
    if (!settings || typeof settings !== 'object') {
      return { error: 'settings_invalid' };
    }

    // Whitelist erlaubter Felder (DB-Spaltennamen)
    const allowedKeys = [
      'enabled',
      'notify_auftraege',
      'notify_chat',
      'notify_erinnerungen',
      'quiet_hours_enabled',
      'quiet_hours_start',
      'quiet_hours_end',
      'quiet_hours_allow_urgent',
    ] as const;

    const update: Record<string, unknown> = {};
    for (const key of allowedKeys) {
      if (key in settings) update[key] = settings[key];
    }
    if (Object.keys(update).length === 0) {
      return { error: 'no_valid_keys' };
    }

    const r = await client
      .from('push_subscriptions')
      .update(update)
      .eq('id', id)
      .select()
      .single();
    return {
      data: r.data as Record<string, unknown> | undefined,
      error: r.error ?? undefined,
    };
  } catch (e: unknown) {
    return { error: e };
  }
}

/**
 * Aktualisiert das `last_used_at`-Feld einer Subscription.
 * Fire-and-forget.
 */
export async function touchPushSubscription(id: string): Promise<void> {
  if (!_sb || !id) return;
  const client = _sb;
  try {
    await client
      .from('push_subscriptions')
      .update({ last_used_at: new Date().toISOString() })
      .eq('id', id);
  } catch {
    /* ignored — Tracking, nicht kritisch */
  }
}

// ════════════════════════════════════════════════════════════════════
// SEND PUSH
// ════════════════════════════════════════════════════════════════════

/**
 * Holt die bevorzugte UI-Sprache eines Users aus `profiles.lang`.
 * Wird vor jedem Push-Versand aufgerufen, damit der Push-Text in
 * der Sprache des Empfängers gerendert wird.
 *
 * Fallback bei jedem Fehler: `'de'` (DRPX-Standard).
 */
export async function getRecipientLang(userId: string): Promise<string> {
  if (!_sb || !userId) return 'de';
  const client = _sb;
  try {
    // KORREKTUR 01.09.2026. Der frühere Kommentar behauptete,
    // `profiles_public` sei für alle angemeldeten Nutzer lesbar. Das galt
    // bis zur Sicherheitshärtung in Migration 050 — seither ist die View
    // `security_invoker = true` und erbt damit die Einschränkung der
    // profiles-Policy: nur das eigene Profil.
    //
    // Der Aufruf lief also seit Mai IMMER ins Leere und gab den Rückfallwert
    // zurück. Folge: Alle Web-Push-Nachrichten gingen auf Deutsch raus,
    // unabhängig von der eingestellten Sprache des Empfängers.
    //
    // Gelesen wird jetzt über die SECURITY-DEFINER-Funktion aus
    // Migration 117, die genau Zustellart und Sprache herausgibt.
    const r = await client.rpc('get_notif_delivery', {
      p_empfaenger: userId,
      p_kategorie: null,
    });
    if (r.error || !r.data) return 'de';
    const row = Array.isArray(r.data) ? r.data[0] : r.data;
    if (!row) return 'de';
    const rec = row as { lang?: string };
    return rec.lang || 'de';
  } catch {
    return 'de';
  }
}

/**
 * Ruft die `send-push` Edge Function auf, um eine Push-Notification
 * an einen User zu schicken. Fire-and-forget — Errors werden geloggt
 * aber nicht geworfen.
 */
export async function invokeSendPush(
  payload: SendPushPayload,
): Promise<{ data?: unknown; error?: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    if (!payload || !payload.user_id || !payload.group || !payload.title || !payload.body) {
      console.warn('[invokeSendPush] missing required fields', payload);
      return { error: 'invalid_payload' };
    }
    const res = await client.functions.invoke('send-push', { body: payload });
    if (res.error) {
      console.warn('[invokeSendPush] error:', res.error);
    }
    return { data: res.data, error: res.error ?? undefined };
  } catch (e: unknown) {
    console.warn('[invokeSendPush] unexpected error:', e);
    return { error: e };
  }
}
